import { checkPresent } from '@yp/common/core.ts';
import { isPresent, recordFromEntries, recordKeys, type MapRec } from '@yp/common/identity.ts';

export const ColorRoles = ['fg', 'bg', 'highlight', 'border'] as const;
export type ColorRole = (typeof ColorRoles)[number];

export type ColorScheme<T extends string> = MapRec<ColorRole, T>;

// export const DaisyColorCodes = [
//   '--color-primary',
//   '--color-primary-content',
//   '--color-secondary',
//   '--color-secondary-content',
//   '--color-accent',
//   '--color-accent-content',
//   '--color-neutral',
//   '--color-neutral-content',
//   '--color-base-100',
//   '--color-base-200',
//   '--color-base-300',
//   '--color-base-content',
//   '--color-info',
//   '--color-info-content',
//   '--color-success',
//   '--color-success-content',
//   '--color-warning',
//   '--color-warning-content',
//   '--color-error',
//   '--color-error-content',
// ] as const;
// export type DaisyColorCode = (typeof DaisyColorCodes)[number];

export function colorVar<T extends string>(
  scheme: ColorScheme<T>,
  role: ColorRole,
  propName?: string,
): string {
  const code = checkPresent(scheme[role], () => `Unknown color scheme role: ${role}`);
  let result: string = code;
  if (code.startsWith('--')) {
    // if (DaisyColorCodes.includes(code as DaisyColorCode)) {
    //   result = `var(${code})`;
    // } else {
    result = `var(${code})`;
    // }
  }
  return isPresent(propName) ? `${propName}: ${result}` : result;
}

/** @deprecated */
export function schemeStyles(scheme: ColorScheme<string>, prefix = 'scheme') {
  return ColorRoles.map((role) => colorVar(scheme, role, `--${prefix}-${role}`)).join('; '); // // `--${prefix}-${cr}: ${colorVar(scheme, cr)}`)
}

const daisyColorScheme = {
  p: 'primary',
  s: 'secondary',
  a: 'accent',
  n: 'neutral',
  b1: 'base-100',
  b2: 'base-200',
  b3: 'base-300',
  in: 'info',
  su: 'success',
  wa: 'warning',
  er: 'error',
} as const;

export const DaisyColorSchemeValues = recordKeys(daisyColorScheme);
export type DaisyColorScheme = (typeof DaisyColorSchemeValues)[number];
export type DaisyRoleScheme = 'def' | Exclude<DaisyColorScheme, 'b1' | 'b2' | 'b3'>;
export const DefaultScheme = 'def';

export const SchemeToTextColor: Record<DaisyRoleScheme, string | undefined> = {
  p: 'text-primary',
  s: 'text-secondary',
  a: 'text-accent',
  n: 'text-neutral',
  in: 'text-info',
  su: 'text-success',
  wa: 'text-warning',
  er: 'text-error',
  def: undefined,
};

export const SchemeToBorderColor: Record<DaisyRoleScheme, string | undefined> = {
  p: 'border-primary',
  s: 'border-secondary',
  a: 'border-accent',
  n: 'border-neutral',
  in: 'border-info',
  su: 'border-success',
  wa: 'border-warning',
  er: 'border-error',
  def: undefined,
};

// export const DaisyColorSchemeLabels: Record<DaisyColorScheme, string> = {
//   p: 'primary',
//   s: 'secondary',
//   a: 'accent',
//   n: 'neutral',
//   b: 'base',
//   in: 'info',
//   su: 'success',
//   wa: 'warning',
//   er: 'error',
// };
//
// export function componentClass(scheme: DaisyColorScheme | undefined, prefix: string) {
//   return isPresent(scheme) ? `${prefix}-${DaisyColorSchemeLabels[scheme]}` : undefined;
// }

export type ComponentSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export const DefaultSize = 'md';

export function colorScheme(scheme: DaisyColorScheme) {
  const color = daisyColorScheme[scheme];
  return {
    color,
    fg: `var(--color-${color}-content)`,
    bg: `var(--color-${color})`,
    highlight: 'var(--color-neutral-content))',
    border: 'var(--color-neutral)',
  };
}

export const DaisyColorMap = recordFromEntries(
  DaisyColorSchemeValues.map((v) => [v, colorScheme(v)]),
  'complete',
) as Record<DaisyColorScheme, ColorScheme<string>>;
